import { useMemo, useSyncExternalStore } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { CalendarCheck, House, ListBullets } from '@phosphor-icons/react';
import { type SavedBooking, savedBookings } from '@/lib/storage';
import { useDesign, useSlug, useStudio } from './StudioRoot';

// Re-render when bookings are saved in this tab (custom event) or another tab (storage event).
export const BOOKINGS_CHANGED = 'beauty:bookings-changed';
function subscribe(cb: () => void) {
  window.addEventListener(BOOKINGS_CHANGED, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(BOOKINGS_CHANGED, cb);
    window.removeEventListener('storage', cb);
  };
}

export function useUpcomingCount(slug: string): number {
  const snapshot = useSyncExternalStore(subscribe, () => {
    const now = Date.now();
    return savedBookings(slug).filter((b) => new Date(b.startsAt).getTime() > now).length;
  });
  return snapshot;
}

/** The nearest upcoming booking saved on this device, or null. */
export function useNextBooking(slug: string): SavedBooking | null {
  const raw = useSyncExternalStore(subscribe, () => {
    const now = Date.now();
    const next = savedBookings(slug)
      .filter((b) => new Date(b.startsAt).getTime() > now)
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0];
    return next ? JSON.stringify(next) : '';
  });
  return useMemo(() => (raw ? (JSON.parse(raw) as SavedBooking) : null), [raw]);
}

export function StudioLayout() {
  const slug = useSlug();
  const { tenant } = useStudio();
  const upcoming = useUpcomingCount(slug);
  const { pathname } = useLocation();
  const design = useDesign();
  const base = `/s/${slug}`;
  // The bright home has its own bottom bar (book + WhatsApp) instead of the navigation.
  const bare = design === 'bright' && (pathname === base || pathname === `${base}/`);
  const items = [
    { to: base, label: 'Главная', icon: House, end: true },
    { to: `${base}/services`, label: 'Услуги', icon: ListBullets },
    { to: `${base}/me`, label: 'Мои записи', icon: CalendarCheck, badge: upcoming },
  ];
  return (
    <>
      {/* Sales demos show no banner: they should look like the studio's own site. */}
      {tenant.status === 'preview' && !tenant.sales_demo && (
        <p className="demo-banner" role="note">
          Демо-версия: записи тестовые
        </p>
      )}
      <main className="app-shell" data-route={pathname} data-bare={bare || undefined}>
        <Outlet />
      </main>
      {!bare && (
        <nav className="bottom-nav glass" aria-label="Навигация" style={{ ['--nav-count' as string]: items.length }}>
          {items.map(({ to, label, icon: Icon, end, badge }) => (
            <NavLink key={to} to={to} end={end} className="nav-item tap">
              {({ isActive }) => (
                <>
                  <Icon size={22} weight={isActive ? 'fill' : 'regular'} aria-hidden />
                  <span>{label}</span>
                  {badge ? (
                    <span className="nav-badge" aria-label={`${badge} предстоящих`}>
                      {badge}
                    </span>
                  ) : null}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}
